import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Suspense, useEffect, useState } from "react";
import { checkIsAdmin } from "@/lib/admin.functions";
import { useAuth } from "@/lib/auth";
import { ArticleTemplateGenerator } from "@/components/admin/tools/ArticleTemplateGenerator";
import { SeoScorecard } from "@/components/admin/tools/SeoScorecard";
import { StrategicFilter } from "@/components/admin/tools/StrategicFilter";
import { CiaMatrix } from "@/components/admin/tools/CiaMatrix";
import { ProductDebtRegister } from "@/components/admin/tools/ProductDebtRegister";
import { StrategicReviewTool } from "@/components/admin/tools/StrategicReviewTool";
import { ExecutiveMeeting } from "@/components/admin/tools/ExecutiveMeeting";
import { HealthScore } from "@/components/admin/tools/HealthScore";
import { SeoRadar } from "@/components/admin/tools/SeoRadar";


export const Route = createFileRoute("/_authenticated/admin/orodja")({
  head: () => ({
    meta: [
      { title: "Strateška orodja — Admin — kupish.si" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Page,
});

type Tool =
  | "article" | "scorecard" | "radar" | "filter" | "cia"
  | "debt" | "review" | "exec" | "health";

const TOOLS: { id: Tool; title: string; group: "content" | "governance" | "strategy"; blurb: string }[] = [
  { id: "article",   group: "content",    title: "Generator predloge članka", blurb: "Editorial Playbook — Dodatek A" },
  { id: "scorecard", group: "content",    title: "SEO Scorecard (≥90/120)",   blurb: "SEO Operating Manual — gate pred objavo" },
  { id: "radar",     group: "content",    title: "SEO Opportunity Radar",     blurb: "Hot 404, zastareli, osiroteli, kanibalizacija" },
  { id: "filter",    group: "governance", title: "Strategic Filter (10 Q)",   blurb: "Strategic Thinking — Dodatek A" },
  { id: "cia",       group: "governance", title: "Change Impact Assessment",  blurb: "Master Operating Prompt — CIA matrika" },
  { id: "debt",      group: "strategy",   title: "Product Debt Register",     blurb: "Product Evolution — živi seznam dolga" },
  { id: "review",    group: "strategy",   title: "Mesečni Strategic Review",  blurb: "Strategic Thinking — Dodatek B" },
  { id: "exec",      group: "strategy",   title: "Tedenski Exec Meeting",     blurb: "Marketplace Playbook — Dodatek B" },
  { id: "health",    group: "strategy",   title: "Product Health Score",      blurb: "Product Evolution — Dodatek B" },
];

function Page() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const checkAdmin = useServerFn(checkIsAdmin);
  const [tool, setTool] = useState<Tool>("article");

  const adminCheck = useQuery({
    queryKey: ["admin-access", user?.id],
    queryFn: () => checkAdmin(),
    enabled: !!user,
    retry: false,
  });

  useEffect(() => {
    if (adminCheck.data?.isAdmin === false) navigate({ to: "/", replace: true });
  }, [adminCheck.data?.isAdmin, navigate]);

  if (!user || adminCheck.isLoading) return <div className="p-6 text-sm text-muted-foreground">Preverjam dostop…</div>;
  if (adminCheck.data?.isAdmin !== true) return null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
      <header className="space-y-1">
        <div className="text-sm text-muted-foreground">
          <Link to="/admin" className="hover:underline">← Admin</Link>
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-brand">Strateška orodja</h1>
        <p className="text-sm text-muted-foreground max-w-3xl">
          Izvedbena plast našega operacijskega sistema: predloge, kontrolne točke, dolgoročna poročila.
          Zgrajena po ustavi projekta, Master Operating Promptu, Editorial Playbooku, SEO Operating Manualu,
          Marketplace Playbooku ter Product Evolution & Strategic Thinking Frameworkih.
        </p>
      </header>

      <nav className="grid gap-2 md:grid-cols-4">
        {(["content", "governance", "strategy"] as const).map((g) => (
          <div key={g} className="md:col-span-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
              {g === "content" ? "Vsebina & SEO" : g === "governance" ? "Upravljanje sprememb" : "Strategija & rast"}
            </div>
            <div className="grid gap-2 md:grid-cols-4">
              {TOOLS.filter((t) => t.group === g).map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTool(t.id)}
                  className={`text-left rounded-lg border p-3 transition ${tool === t.id ? "border-brand bg-brand/5" : "border-border hover:bg-muted"}`}
                >
                  <div className="text-sm font-medium">{t.title}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t.blurb}</div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <section>
        <Suspense fallback={<div className="p-6 text-sm text-muted-foreground">Nalagam orodje…</div>}>
          {tool === "article"   && <ArticleTemplateGenerator />}
          {tool === "scorecard" && <SeoScorecard />}
          {tool === "radar"     && <SeoRadar />}
          {tool === "filter"    && <StrategicFilter />}
          {tool === "cia"       && <CiaMatrix />}
          {tool === "debt"      && <ProductDebtRegister />}
          {tool === "review"    && <StrategicReviewTool />}
          {tool === "exec"      && <ExecutiveMeeting />}
          {tool === "health"    && <HealthScore />}
        </Suspense>
      </section>
    </div>
  );
}
